import { useSession } from '@airship-platform/airship-auth';
import { BackButton } from '@airship-platform/airship-patterns';
import {
  FlexBox,
  LoadingButton,
  NumberInput,
  Spacer,
} from '@airship-platform/jam-web/components';
import Divider from '@mui/material/Divider';
import { PropsWithChildren, useState } from 'react';
import { type DurationBillingItem, ManageableProduct } from '../../data-access';
import DurationBillingSelect from './DurationBillingSelect';
import { canEditPrice } from './EditProductPriceForm';
import PriceSectionEditable from './PriceSection';

interface ManageProductTechnicianContentProps {
  product: ManageableProduct;
  hasMembership: boolean;
  isLoading: boolean;
  isDisabled?: boolean;
  hasError?: boolean;
  priceIsEditable?: boolean;
  submitButtonText?: string;
  durationBillingItems?: DurationBillingItem[];
  onDelete?: () => void;
  onCancel: () => void;
  onSubmit: () => void;
  onUpdateProduct: (changes: Partial<ManageableProduct>) => void;
}

const ManageProductTechnicianContent: React.FC<
  PropsWithChildren<ManageProductTechnicianContentProps>
> = ({
  product,
  hasMembership,
  isLoading,
  isDisabled,
  hasError,
  priceIsEditable,
  submitButtonText,
  durationBillingItems,
  children,
  onDelete,
  onCancel,
  onSubmit,
  onUpdateProduct,
}) => {
  const { claims } = useSession();
  const [isPriceValid, setIsPriceValid] = useState(true);

  const durationItems = durationBillingItems ?? [];
  const selectedDurationBillingId =
    product.durationBillingId || durationItems[0]?.durationBillingId;

  return (
    <FlexBox flexDirection={'column'} width={'100%'} gap={3}>
      <PriceSectionEditable
        isEditable={priceIsEditable !== false}
        isDisabled={isDisabled}
        prices={product.prices}
        defaultPriceType={product.defaultPriceType}
        displayPriceOptions={true}
        hasMembership={hasMembership}
        canEditPrice={canEditPrice(claims, product)}
        quantityComponent={
          <NumberInput
            disabled={isLoading || isDisabled}
            initialValue={product.quantity ?? 1}
            onChange={(val) => onUpdateProduct({ quantity: val })}
            onDelete={onDelete || onCancel}
          />
        }
        onUpdateProduct={onUpdateProduct}
        onPriceValidityChange={setIsPriceValid}
      />

      {selectedDurationBillingId && (
        <DurationBillingSelect
          items={durationItems}
          selectedDurationBillingId={selectedDurationBillingId}
          isDisabled={isDisabled}
          onChange={(item) =>
            onUpdateProduct({
              durationBillingId: item.durationBillingId,
              ...(item.useMembershipPricingTable !== false && {
                price: item.salePrice,
                prices: product.prices.map((p) =>
                  p.priceType === product.defaultPriceType
                    ? { ...p, value: item.salePrice }
                    : p
                ),
              }),
            })
          }
        />
      )}

      {children && <Divider />}

      {children}

      <FlexBox gap={2} justifyContent={'space-between'}>
        <BackButton
          color="softGold"
          size={'large'}
          sx={{ minWidth: '8rem' }}
          defaultPath="../"
        >
          Cancel
        </BackButton>
        <LoadingButton
          onClick={onSubmit}
          loading={isLoading}
          disabled={isDisabled || !isPriceValid}
          error={hasError}
          size={'large'}
          sx={{ minWidth: '8rem' }}
        >
          {hasError ? 'Retry' : submitButtonText || 'Add'}
        </LoadingButton>
      </FlexBox>
      <Spacer />
    </FlexBox>
  );
};

export default ManageProductTechnicianContent;
